import React from "react";
import { connect } from "react-redux";
import GroqImage from "../containers/images/groq.png";
import MetaImage from "../containers/images/meta.png";
import AwsLogo from "../containers/images/aws.png";
import TravelImage from "../containers/images/travel.webp";
import DiningImage from "../containers/images/dining.webp";
import RecImage from "../containers/images/rec.webp";
import CopterImage from "../containers/images/copter.webp";
import ShoppingImage from "../containers/images/shopping.webp";
import Navbar from "../components/Navbar";
import { heroImageSrcSet, heroImageUrl } from "../utils/preloadImages";
import { readCachedOnboardingCompletion } from "../utils/flowGuards";
import { EXPERIMENT_LANDING_HERO, useFunnelExperiment } from "../utils/funnelExperiments";
import { HOME_HERO_PROMISE, HOME_SAMPLE_PATH, HOME_SAMPLE_PREVIEW, HOME_STEPS } from "../utils/homeCopy";
import { trackSignupCta } from "../utils/publicAnalytics";
import {
  LANDING_HEADLINE_FALLBACK_VARIANT,
  LANDING_HERO_HEADLINE,
  LANDING_HERO_SUBHEAD,
  STORY_SAMPLE_LINK_LABEL,
  currentLandingSearch,
  landingHeadlineCopy,
  storySampleHref,
} from "../utils/storyHeadlineCopy";
import { storyHeadlineEnabled } from "../utils/storyHeadlineFlag";
import "./Home.css";

const imageMoments = [
  {
    title: "Local restaurants",
    text: "Neighborhood spots and dinners that match your taste, pace, and the kind of night you want.",
    image: DiningImage,
    width: 1200,
    height: 900,
  },
  {
    title: "Crafts and shops",
    text: "Small makers, markets, and finds that feel local instead of tourist-default.",
    image: ShoppingImage,
    width: 1200,
    height: 900,
  },
  {
    title: "Once-in-a-lifetime events",
    text: "Shows, festivals, and rare stops timed to your trip and what you actually care about.",
    image: RecImage,
    width: 2048,
    height: 1987,
  },
  {
    title: "Full itineraries",
    text: "Day-by-day plans pulled together so you are not comparing every option yourself.",
    image: TravelImage,
    width: 1200,
    height: 760,
  },
];

const values = [
  {
    title: "Adapts in the moment",
    text: "When weather shifts, a venue closes, or energy drops, Dople adjusts the plan without making you start over.",
  },
  {
    title: "Personalized alternatives",
    text: "Every setback comes with options that still fit your taste, pace, and the trip you meant to take.",
  },
  {
    title: "Proactive, not reactive",
    text: "Dople watches for friction ahead of time and offers a better path before the day falls apart.",
  },
];

const lisbonMoments = [
  {
    when: "Dawn",
    title: "Viewpoint",
    text: "A Miradouro overlook with a sit-down break, and a tram if the hill is more than you want.",
  },
  {
    when: "Midday",
    title: "Lunch near the morning",
    text: "A tasca or seafood lunch in the same pocket as the market browse, so the afternoon stays close.",
  },
  {
    when: "Sunset",
    title: "Dinner near base",
    text: "Neighborhood seafood back in Baixa and Chiado. A short stroll, then an early night.",
  },
];

const tripExamples = [
  {
    title: "Four days in Lisbon, slow mornings",
    text: "Pastry and coffee near the hotel, one big sight before lunch, a long seafood lunch, and dinner close enough to walk back.",
  },
  {
    title: "A food weekend in Valencia",
    text: "Central Market at opening, a paella class instead of a tourist paella, and the old town at golden hour.",
  },
  {
    title: "Porto with a vegetarian and a hiker",
    text: "Meat-free picks that still feel local, one Douro day with a real trail, and nothing scheduled before 9:30.",
  },
];

const trustItems = [
  "You control what Dople remembers.",
  "The survey is the personalization. The itinerary stays a trip.",
  "What you tell Dople shapes the plan, without extra noise.",
];

const Home = ({ isAuthenticated }) => {
  const heroVariant = useFunnelExperiment(EXPERIMENT_LANDING_HERO);
  const samplePreview = heroVariant === "sample_preview";
  // Read the query during render. The first paint is the chosen hero, with no later swap.
  const storyHero = storyHeadlineEnabled() ? landingHeadlineCopy(currentLandingSearch()) : null;
  const storySampleLink = storyHero && storyHero.variant !== LANDING_HEADLINE_FALLBACK_VARIANT
    ? storySampleHref(currentLandingSearch())
    : "";
  const heroClassName = storySampleLink ? "home-hero story-hero" : "home-hero";
  const surveyFinished = isAuthenticated && readCachedOnboardingCompletion() === true;
  const ctaHref = !isAuthenticated ? "/signup" : surveyFinished ? "/trips" : "/survey";
  const ctaLabel = !isAuthenticated ? "Plan Your Trip" : surveyFinished ? "Open Trips" : "Continue setup";

  return (
    <div className="home-page">
      <Navbar />

      <section className={heroClassName}>
        <img
          className="home-hero-photo"
          src={heroImageUrl()}
          srcSet={heroImageSrcSet()}
          sizes="(max-width: 767px) 100vw, 1800px"
          alt=""
          width={1800}
          height={1210}
          fetchPriority="high"
          decoding="sync"
        />
        <div className="home-hero-scrim" aria-hidden="true" />
        <div className="home-hero-inner">
          {samplePreview ? (
            <article className="hero-sample-preview" aria-label="Sample itinerary preview">
              <span className="hero-sample-kicker">{HOME_SAMPLE_PREVIEW.kicker}</span>
              <h2>{HOME_SAMPLE_PREVIEW.title}</h2>
              <p>{HOME_SAMPLE_PREVIEW.note}</p>
              <ol>
                {HOME_SAMPLE_PREVIEW.days.map((day) => (
                  <li key={day.label}>
                    <strong>{day.label}.</strong> {day.text}
                  </li>
                ))}
              </ol>
            </article>
          ) : (
            <span className="hero-kicker">Personalized travel itineraries</span>
          )}
          <h1>{samplePreview ? HOME_SAMPLE_PREVIEW.headline : (storyHero ? storyHero.headline : LANDING_HERO_HEADLINE)}</h1>
          <p className="hero-subtitle">
            {samplePreview
              ? HOME_SAMPLE_PREVIEW.subtitle
              : (storyHero ? storyHero.subhead : LANDING_HERO_SUBHEAD)}
          </p>
          {storySampleLink ? (
            <a
              className="story-sample-link"
              href={storySampleLink}
              onClick={() => trackSignupCta("story_sample_link", storySampleLink)}
            >
              {STORY_SAMPLE_LINK_LABEL}
            </a>
          ) : null}
          <div className="hero-actions">
            <a href={ctaHref} className="cta-button primary" onClick={() => trackSignupCta("landing_hero", ctaHref)}>
              <strong>{ctaLabel}</strong>
            </a>
          </div>
          <p className="hero-promise">{HOME_HERO_PROMISE}</p>
          <ol className="hero-steps" aria-label="How Dople works">
            {HOME_STEPS.map((step, index) => (
              <li key={step}>
                <span className="hero-step-number">{index + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          {storySampleLink ? null : (
            <a className="hero-sample-link" href={HOME_SAMPLE_PATH}>
              See a sample itinerary
            </a>
          )}
          <div className="hero-trust-row" aria-label="Trusted technology partners">
            <span>Supported by</span>
            <img className="trust-logo meta" src={MetaImage} alt="Meta" width={149} height={84} decoding="async" fetchPriority="low" />
            <img className="trust-logo aws" src={AwsLogo} alt="AWS" width={105} height={63} decoding="async" fetchPriority="low" />
            <img className="trust-logo groq" src={GroqImage} alt="Groq" width={78} height={78} decoding="async" fetchPriority="low" />
          </div>
        </div>
      </section>

      <section className="editorial-intro">
        <p>
          An AI travel agent that researches restaurants, local crafts and shops,
          and once-in-a-lifetime events that fit your preferences, habits, and
          lifestyle, then builds a personalized itinerary.
        </p>
      </section>

      <section className="lifestyle-strip" aria-label="What Dople researches">
        {imageMoments.map((item) => (
          <article key={item.title} className="lifestyle-frame">
            <img
              className="lifestyle-frame-photo"
              src={item.image}
              alt=""
              width={item.width}
              height={item.height}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
            />
            <div>
              <span>{item.title}</span>
              <p>{item.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="home-section value-section">
        <div className="section-heading">
          <span className="section-kicker">What changes</span>
          <h2>Your trip keeps working when the day does not.</h2>
        </div>
        <div className="value-grid">
          {values.map((item) => (
            <article key={item.title} className="value-statement">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="concierge-section">
        <div className="concierge-image">
          <img
            className="concierge-image-photo"
            src={CopterImage}
            alt=""
            width={1800}
            height={937}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
        </div>
        <div className="concierge-copy">
          <span className="section-kicker">Trips Dople plans</span>
          <h2>Built around how you actually travel.</h2>
          <ul className="trip-example-list">
            {tripExamples.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="itinerary-moments" aria-label="Sample Lisbon itinerary moments">
        <div className="section-heading centered">
          <span className="section-kicker">Sample itinerary</span>
          <h2>Three stops from a Lisbon day.</h2>
          <p>{HOME_SAMPLE_PREVIEW.note}</p>
        </div>
        <div className="itinerary-moments-grid">
          {lisbonMoments.map((item) => (
            <article key={item.title} className="itinerary-moment">
              <span>{item.when}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="trust-section">
        <div>
          <span className="section-kicker">Trust</span>
          <h2>Personalization should feel supportive, never invasive.</h2>
        </div>
        <ul>
          {trustItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="final-cta">
        <img
          className="final-cta-photo"
          src={ShoppingImage}
          alt=""
          width={1200}
          height={900}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
        />
        <div className="final-cta-scrim" aria-hidden="true" />
        <div>
          <span className="section-kicker">Begin</span>
          <h2>Less time researching. More time traveling.</h2>
          <p>
            Create a free profile and get a trip plan that already fits how you
            travel, eat, and move through a place.
          </p>
          <a href={ctaHref} className="cta-button primary" onClick={() => trackSignupCta("landing_footer", ctaHref)}>
            <strong>{ctaLabel}</strong>
          </a>
        </div>
      </section>

      <footer className="home-footer">
        <div className="footer-container">
          <div className="footer-links">
            <a href="/privacy">
              Privacy Policy
            </a>
            <a href="/terms">
              Terms of Service
            </a>
            <a href="/contact">Contact</a>
          </div>
          <p>© {new Date().getFullYear()} Dople Technologies LLC.</p>
        </div>
      </footer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps)(Home);
